/* eslint-disable @typescript-eslint/no-magic-numbers */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { AtHomeLoginResponse } from '@/services/api';

import { useButtonDialogBox } from './useButtonDialogBox';

const useAutoSignout = () => {
  const { showButtonDialogBox, closeDialog } = useButtonDialogBox();
  const { t } = useTranslation(['task', 'translation']);
  const [shouldRedirectToRoot, setShouldRedirectToRoot] = useState(false);
  const hasShownDialogReference = useRef(false);
  const timeoutReference = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSessionExpiry = useCallback(() => {
    if (hasShownDialogReference.current) return;
    hasShownDialogReference.current = true;

    if (timeoutReference.current) {
      clearTimeout(timeoutReference.current);
    }

    showButtonDialogBox({
      open: true,
      title: t('translation:loggedOut'),
      subtitle: t('translation:loggedOutDueToLogout'),
      rightButtonText: t('task:okButton'),
      onRightButtonClick: closeDialog,
      onClose: () => {
        const deviceId = localStorage.getItem('device-id');
        localStorage.clear();
        sessionStorage.clear();
        if (deviceId) {
          localStorage.setItem('device-id', deviceId);
        } else {
          localStorage.setItem('device-id', uuidv4());
        }
        setShouldRedirectToRoot(true);
      },
      zIndex: 50,
      variant: 'blue',
      showCloseButton: false,
    });
  }, [showButtonDialogBox, closeDialog, t]);

  useEffect(() => {
    const accessLocation = localStorage.getItem('accessLocation');
    if (accessLocation !== 'home') return;

    const response = localStorage.getItem('atHomeLoginResponse');
    if (!response) return;

    let expiryTime: number;
    try {
      const atHomeLoginResponse = JSON.parse(response) as AtHomeLoginResponse;
      if (!atHomeLoginResponse?.refreshTokenExpiryTime) return;
      expiryTime = new Date(atHomeLoginResponse.refreshTokenExpiryTime).getTime();
    } catch (error) {
      console.error('Failed to parse atHomeLoginResponse', error);
      return;
    }

    hasShownDialogReference.current = false;

    const delay = expiryTime - Date.now();
    if (delay <= 0) {
      handleSessionExpiry();
      return;
    }
    timeoutReference.current = setTimeout(() => {
      handleSessionExpiry();
    }, delay);

    // eslint-disable-next-line consistent-return
    return () => {
      if (timeoutReference.current) {
        clearTimeout(timeoutReference.current);
      }
    };
  }, [handleSessionExpiry]);

  return { shouldRedirectToRoot };
};

export default useAutoSignout;
